import React, { useMemo, useRef } from "react";
import { withTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { Box, Grid, InputAdornment, Typography, useTheme } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import { get } from "lodash";
import PropTypes from "prop-types";

import Tooltip from "Components/Common/Tooltip";
import YesNoRadioButton from "Components/RegisterBuildingForm/YesNoRadioButton";
import SelectField from "Components/Common/SelectField";
import InputFieldText from "Components/Common/InputFieldText";
import { fireVentilationTypeEnum } from "common/enums";
import RefugeAreaDetails from "./RefugeAreaDetails";

import { getImageUrl, onlyNumberKeys, resetFieldValue } from "utils";
import getFileExtension from "utils/getFileExtension";

import { DocumentName, UploadImage } from "./style";

import pdfImg from "assets/images/pdf.png";

const validFileFormats = ["jpg", "png", "jpeg", "pdf"];

const FireLifeSafety = ({ formik, t }) => {
  const {
    errors,
    touched,
    setFieldValue,
    values: {
      lifeSafety: {
        numberOfInternalStaircases,
        isBasements,
        dbrDocuments,
        isQualifiedFireOfficer,
      },
    },
  } = formik;
  const theme = useTheme();
  const ref = useRef();

  const errorMessage = useMemo(
    () => get(errors, "lifeSafety.dbrDocuments", ""),
    [get(errors, "lifeSafety.dbrDocuments", "")]
  );

  const isError = useMemo(
    () => !!get(touched, "lifeSafety.dbrDocuments", "") && !!errorMessage,
    [get(touched, "lifeSafety.dbrDocuments", ""), errorMessage]
  );

  const fileChangeHandler = (e) => {
    const files = Array.from(e.target.files);
    const isAllFileValid = files.every(
      (file) =>
        validFileFormats.includes(getFileExtension(file.name.toLowerCase())) &&
        file.size < 10485760
    );

    if (!isAllFileValid) {
      toast.error(
        "Please upload image or pdf file and size should be less than 10 mb"
      );
    } else {
      setFieldValue("lifeSafety.dbrDocuments", [
        ...dbrDocuments,
        ...files.map((file) => ({
          id: 0,
          url: URL.createObjectURL(file),
          name: file.name,
          file: file,
        })),
      ]);
    }
    ref.current.value = "";
  };

  return (
    <Grid container columnSpacing={1} rowSpacing={1}>
      <Grid container rowSpacing={1} columnSpacing={2} sx={{ pl: 1 }}>
        <YesNoRadioButton
          label="Whether any compartmentation has been done in the building?"
          keyName="lifeSafety.isCompartmentationDone"
          formik={formik}
          isRequired
        />
      </Grid>
      <Grid container rowSpacing={1} columnSpacing={2} sx={{ pl: 1 }}>
        <YesNoRadioButton
          label="Whether a lightning arrestor has been installed?"
          keyName="lifeSafety.isLightningArrestorInstalled"
          formik={formik}
          isRequired
        />
      </Grid>
      <Grid container rowSpacing={1} columnSpacing={2} sx={{ pl: 1 }}>
        <Grid item>
          <Typography sx={{ pt: 1 }}>
            {t(
              "Number of internal staircases that have been provided in the building block"
            )}
          </Typography>
        </Grid>
        <Grid item xs={12} sm={6} md={4} lg={3}>
          <InputFieldText
            label={t("Number of internal staircase") + "*"}
            name="lifeSafety.numberOfInternalStaircases"
            formik={formik}
            placeholder="Enter Number"
            type="number"
            InputProps={{
              inputProps: {
                min: 0,
                onKeyDown: (e) => onlyNumberKeys(e, true),
              },
            }}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4} lg={3}>
          <InputFieldText
            label={t("Width") + "*"}
            name="lifeSafety.internalStaircaseWidth"
            formik={formik}
            placeholder="Enter Width"
            type="number"
            disabled={!numberOfInternalStaircases}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">Mtrs</InputAdornment>
              ),
              inputProps: {
                min: 0,
                onKeyDown: (e) => onlyNumberKeys(e, true),
              },
            }}
          />
        </Grid>
      </Grid>
      <Grid container rowSpacing={1} columnSpacing={2} sx={{ pl: 1 }}>
        <Grid item>
          <Typography sx={{ pt: 1 }}>
            {t(
              "Number of external staircases that have been provided in the building"
            )}
          </Typography>
        </Grid>
        <Grid item xs={12} sm={6} md={4} lg={3}>
          <InputFieldText
            label={t("Number")}
            name="lifeSafety.numberOfExternalStaircases"
            formik={formik}
            placeholder="Enter Number"
            type="number"
            InputProps={{
              inputProps: {
                min: 0,
                onKeyDown: (e) => onlyNumberKeys(e, true),
              },
            }}
          />
        </Grid>
      </Grid>
      <Grid container rowSpacing={1} columnSpacing={2} sx={{ pl: 1 }}>
        <YesNoRadioButton
          label="Have fire exit doors been provided in building?"
          keyName="lifeSafety.isFireExitDoors"
          formik={formik}
          isRequired
        />
      </Grid>

      <Grid container rowSpacing={1} columnSpacing={2} sx={{ pl: 1 }}>
        <YesNoRadioButton
          label="Are there any basements in the building?"
          keyName="lifeSafety.isBasements"
          onChange={(e) => {
            if (!+e.target.value) {
              resetFieldValue(setFieldValue, [
                "lifeSafety.isBasementHasSprinklers",
                "lifeSafety.numberOfBasements",
                "lifeSafety.basementVentilationType",
              ]);
            }
            setFieldValue("lifeSafety.isBasements", !!+e.target.value);
          }}
          formik={formik}
          isRequired
        />
      </Grid>

      {isBasements && (
        <Grid container rowSpacing={1} columnSpacing={2} sx={{ pl: 1 }}>
          <YesNoRadioButton
            label="Are basements sprinklered?"
            keyName="lifeSafety.isBasementHasSprinklers"
            formik={formik}
            isRequired
          />
          <Grid item xs={12} sm={6} md={4} lg={3}>
            <InputFieldText
              label={t("Number of basement provided") + "*"}
              name="lifeSafety.numberOfBasements"
              formik={formik}
              placeholder="Enter Number of Basements"
              type="number"
              InputProps={{
                inputProps: {
                  min: 0,
                  onKeyDown: (e) => onlyNumberKeys(e, true),
                },
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4} lg={3}>
            <SelectField
              label={t("Type of Ventilation") + "*"}
              name="lifeSafety.basementVentilationType"
              formik={formik}
              options={fireVentilationTypeEnum}
            />
          </Grid>
        </Grid>
      )}

      <Grid container rowSpacing={1} columnSpacing={2} sx={{ pl: 1 }}>
        <YesNoRadioButton
          label="Whether adequate illumination with alternate power supply is provided for safe movement of persons towards and through the exits?"
          keyName="lifeSafety.isAlternatePowerSupply"
          formik={formik}
          isRequired
        />
      </Grid>
      <Grid container rowSpacing={1} columnSpacing={2} sx={{ pl: 1 }}>
        <YesNoRadioButton
          label="Whether proper exit signage has been provided in exit access area to provide clear directions to people moving towards exits?"
          keyName="lifeSafety.isExitSigns"
          formik={formik}
          isRequired
        />
      </Grid>
      <Grid container rowSpacing={1} columnSpacing={2} sx={{ pl: 1 }}>
        <Grid item xs={12} sm={6} md={4} lg={3}>
          <InputFieldText
            label={t("Assembly Point Details")}
            name="lifeSafety.assemblyPointDetail"
            formik={formik}
            placeholder="Enter assembly point details"
          />
        </Grid>
      </Grid>
      <Grid item xs={12}>
        <Typography>
          {t("Provide approved DBR & Firefighting drawings of building")}
          {<span className="text-danger">*</span>}
        </Typography>
      </Grid>
      <Grid item xs={12} className="mb-3 ms-1 mt-1">
        <div className="d-flex gap-3 flex-wrap">
          {!!dbrDocuments.length &&
            dbrDocuments.map(({ url, name, file }, index) => (
              <div className="image-container" key={index}>
                <a rel="noreferrer">
                  <Box
                    component="img"
                    className="image d-block m-auto"
                    alt="document image"
                    src={
                      getFileExtension(name).toLowerCase() === "pdf"
                        ? pdfImg
                        : file
                        ? url
                        : getImageUrl(url)
                    }
                  />
                </a>
                <i
                  className="ri-close-circle-line fs-3 close-btn"
                  onClick={() => {
                    setFieldValue(
                      "lifeSafety.dbrDocuments",
                      dbrDocuments.filter((_, ind) => ind !== index)
                    );
                  }}
                />
                <Tooltip title={name}>
                  <DocumentName fontSize={13} variant="body2">
                    {name}
                  </DocumentName>
                </Tooltip>
              </div>
            ))}

          <Box
            component="label"
            className="mb-3 p-1 cursor-pointer"
            sx={{
              border: "1px dashed black",
            }}
          >
            <UploadImage>
              <AddIcon />
            </UploadImage>
            <input
              ref={ref}
              type="file"
              accept="image pdf/*"
              onChange={fileChangeHandler}
              multiple
              hidden
            />
          </Box>
          {isError && (
            <Box>
              <Typography
                variant="subtitle2"
                sx={{ color: theme.palette.error.main, pt: 1.2 }}
              >
                {t(errorMessage)}
              </Typography>
            </Box>
          )}
        </div>
      </Grid>
      <Grid container rowSpacing={1} columnSpacing={2} sx={{ pl: 1 }}>
        <YesNoRadioButton
          label="Is building have appointed qualified Fire Officer?"
          keyName="lifeSafety.isQualifiedFireOfficer"
          formik={formik}
          onChange={(e) => {
            resetFieldValue(setFieldValue, [
              "lifeSafety.fireOfficerName",
              "lifeSafety.fireOfficerMobileNumber",
              "lifeSafety.fireOfficerEmailId",
            ]);
            setFieldValue(
              "lifeSafety.isQualifiedFireOfficer",
              !!+e.target.value
            );
          }}
          isRequired
        />
      </Grid>
      {isQualifiedFireOfficer && (
        <>
          <Grid container rowSpacing={1} columnSpacing={2} sx={{ pl: 1 }}>
            <Grid item xs={12} sm={6} md={4} lg={3}>
              <InputFieldText
                label={t("Name")}
                name="lifeSafety.fireOfficerName"
                formik={formik}
                placeholder="Enter Name"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4} lg={3}>
              <InputFieldText
                label={t("Mobile Number") + "*"}
                name="lifeSafety.fireOfficerMobileNumber"
                type="number"
                formik={formik}
                placeholder="Enter Mobile Number"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4} lg={3}>
              <InputFieldText
                label={t("Email") + "*"}
                name="lifeSafety.fireOfficerEmailId"
                formik={formik}
                placeholder="Enter Email"
              />
            </Grid>
          </Grid>
        </>
      )}
      <RefugeAreaDetails formik={formik} />
    </Grid>
  );
};

FireLifeSafety.propTypes = {
  formik: PropTypes.object,
  t: PropTypes.func,
};

export default withTranslation()(FireLifeSafety);
